import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Typography,
  Space,
  Select,
  DatePicker,
  Button,
  message,
  Row,
  Col,
  Tag,
  theme,
  Tooltip,
} from 'antd';
import {
  ReloadOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { billingApi, userApi } from '../../services/api';
import { BillingRecord, User } from '../../types';
import { usePermissionCheck } from '../../context/PermissionContext';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const BillingRecords: React.FC = () => {
  const [records, setRecords] = useState<BillingRecord[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { token } = theme.useToken();
  const { canViewUsers } = usePermissionCheck();

  // Filters
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>();
  const [selectedModel, setSelectedModel] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  useEffect(() => {
    if (canViewUsers()) {
        fetchUsers();
    }
    // 移除 fetchRecords()，由下方的 useEffect 统一触发，避免重复请求
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [currentPage, pageSize, selectedUserId, selectedModel, dateRange]);

  const fetchUsers = async () => {
    try {
      const response = await userApi.getUsers({ page: 1, page_size: 100 });
      setUsers(response.data.users);
    } catch (error) {
      // ignore
    }
  };

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        page_size: pageSize,
        user_id: selectedUserId,
        model_name: selectedModel,
        start_time: dateRange && dateRange[0] ? dateRange[0].startOf('day').toISOString() : undefined,
        end_time: dateRange && dateRange[1] ? dateRange[1].endOf('day').toISOString() : undefined,
      };
      const response = await billingApi.getRecords(params);
      setRecords(response.data.records);
      setTotal(response.data.total);
    } catch (error) {
      message.error('获取消费记录失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchRecords();
  };

  const handleClearFilters = () => {
    setSelectedUserId(undefined);
    setSelectedModel(undefined);
    setDateRange(null);
    setCurrentPage(1);
  };

  const columns: ColumnsType<BillingRecord> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: '用户',
      dataIndex: 'user_id',
      width: 120,
      render: (userId: number, record) => {
        const user = users.find(u => u.id === userId) || record.user;
        return (
          <div>
            <div>{user ? user.name : userId}</div>
            {user && <div style={{ fontSize: '12px', color: token.colorTextSecondary }}>ID: {userId}</div>}
          </div>
        );
      },
    },
    {
      title: '模型',
      dataIndex: 'model_name',
      width: 150,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: '类型',
      dataIndex: 'type',
      width: 100,
      render: (type) => (
          type === 'recharge' ? <Tag color="green">充值</Tag> : <Tag color="geekblue">API调用</Tag>
      )
    },
    {
        title: 'Token消耗',
        dataIndex: 'token_count',
        render: (val, record) => (
            <Tooltip title={
                <div>
                    <div>输入: {record.input_tokens.toLocaleString()}</div>
                    <div>输出: {record.output_tokens.toLocaleString()}</div>
                </div>
            }>
                <span>{val > 0 ? val.toLocaleString() : '-'}</span>
            </Tooltip>
        ),
    },
    {
      title: '金额',
      dataIndex: 'cost',
      render: (cost: number, record) => {
          const color = record.type === 'recharge' ? token.colorSuccess : token.colorError;
          const prefix = record.type === 'recharge' ? '+' : '-';
          const content = (
             <span style={{ color }}>
                  {prefix}¥{Math.abs(cost).toFixed(4)}
              </span>
          );

          if (record.type === 'recharge') {
              return content;
          }

          return (
            <Tooltip title={
                <div>
                    <div>输入: ¥{record.input_cost?.toFixed(6) || '0.000000'}</div>
                    <div>输出: ¥{record.output_cost?.toFixed(6) || '0.000000'}</div>
                </div>
            }>
              {content}
            </Tooltip>
          );
      },
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2}>消费记录管理</Title>
          <Space>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
            >
              清除筛选
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
            >
              刷新
            </Button>
          </Space>
        </div>

        {/* Filters */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
                <div>
                  <div style={{ marginBottom: 4, fontWeight: 500 }}>用户筛选</div>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="选择用户"
                    value={selectedUserId}
                    onChange={setSelectedUserId}
                    allowClear
                    showSearch
                    filterOption={(input, option) => {
                      const user = users.find(u => u.id === option?.value);
                      if (!user) return false;
                      const searchText = `${user.name} ${user.id}`.toLowerCase();
                      return searchText.includes(input.toLowerCase());
                    }}
                  >
                    {users.map(user => (
                      <Option key={user.id} value={user.id}>
                        {user.name} (ID: {user.id})
                      </Option>
                    ))}
                  </Select>
                </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
                <div>
                    <div style={{ marginBottom: 4, fontWeight: 500 }}>模型筛选</div>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="选择模型"
                        value={selectedModel}
                        onChange={setSelectedModel}
                        allowClear
                    >
                       <Option value="deepseek-chat">deepseek-chat</Option>
                       <Option value="deepseek-reasoner">deepseek-reasoner</Option>
                       <Option value="gpt-4">gpt-4</Option>
                       <Option value="gpt-3.5-turbo">gpt-3.5-turbo</Option>
                       <Option value="claude-3-5-sonnet">claude-3-5-sonnet</Option>
                    </Select>
                </div>
            </Col>
             <Col xs={24} sm={12} md={6}>
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>时间范围</div>
                <RangePicker
                  style={{ width: '100%' }}
                  value={dateRange}
                  onChange={setDateRange}
                />
              </div>
            </Col>
          </Row>
        </Card>

        <Table
          columns={columns}
          dataSource={records}
          loading={loading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size || 10);
            },
          }}
        />
      </Card>
    </div>
  );
};

export default BillingRecords;
